<template>
    <div class="chat-window d-flex flex-column h-100">
        <chat-header
            :user="chat.participants[0].user"
            :chatId="chat.id"
            :status="chat.status"
            :isWorkfixLine="chat.is_workfix_line === true"
            :readOnly="pickedUp"
            @transfer="$emit('transfer')"
            @request-close="onRequestCloseChat"
            @inbox-archive="onInboxArchive"
        />
        <p v-if="pickedUp" class="picked-up-note mt-3 mb-0 px-2">{{ pickedUpNote }}</p>
        <div class="messages-container mt-4 mb-4">
            <div v-if="earlier.length" class="earlier-conversations mb-3">
                <button v-if="!showEarlier" type="button" class="btn btn-link p-0 show-earlier" @click="showEarlier = true">
                    Show earlier conversations ({{ earlier.length }})
                </button>
                <template v-else>
                    <div v-for="conversation in earlier" :key="conversation.chat.id" class="earlier-conversation">
                        <p class="earlier-heading text-muted text-center my-2">
                            Earlier conversation · {{ conversation.chat.category }} · {{ dayOf(conversation.chat.created_at) }}
                        </p>
                        <chat-messages :messages="conversation.messages" />
                    </div>
                </template>
            </div>
            <chat-messages :messages="messages" :isWorkfixLine="chat.is_workfix_line === true" />
        </div>
        <p v-if="sendFailure && !pickedUp" class="send-failure mb-2 px-2">{{ sendFailure }}</p>
        <chat-input
            ref="input"
            :status="chat.status"
            :sending="sending"
            :locked="pickedUp"
            @send="sendMessage"
            @upload="uploadFile"
        />
    </div>
</template>
  
<script>      
    import Vue from 'vue';
    import ChatHeader from './ChatHeader.vue'
    import ChatMessages from './ChatMessages.vue'
    import ChatInput    from './ChatInput.vue'
    import store from '../../store.js'
    import { Can } from '../../../permission/ability.js'
    import { RIGHTS } from '../../views/roles/rights'
    import { CONFLICT, FORBIDDEN, NOT_FOUND, describeApiOutcome } from '../../utils/apiOutcome.js'
    import { colleagueHolding, pickedUpNote } from '../../utils/chatClaim.js'
    import moment from 'moment'

    const CLAIMED_BY_SOMEONE_ELSE = 'Someone else is handling this chat now.';

    const ARCHIVE_FAILED = 'The chat could not be archived. Please try again.';

    const NO_LONGER_YOURS_TO_ANSWER = [FORBIDDEN, NOT_FOUND, CONFLICT];

    export default {
        name: 'ChatWindow',
        components: {
            ChatHeader,
            ChatMessages,
            ChatInput
        },
        props: {
            chat: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                rights: '',
                isAdmin: false,
                messages: [],
                earlier: [],
                showEarlier: false,
                sending: false,
                sendFailure: ''
            }
        },
        computed: {
            holdingColleague() {
                return colleagueHolding(this.chat, this.$store.getters.getHourAppUser);
            },
            pickedUp() {
                return this.holdingColleague !== null;
            },
            pickedUpNote() {
                return pickedUpNote(this.holdingColleague);
            }
        },
        async created() {
            this.rights = RIGHTS;
            await Promise.all([this.fetchData(), this.fetchEarlier()]);
            this.isAdmin = this.$store.state.users.me.admin;
        },
        watch: {
            chat: function(fresh, previous) {
                this.fetchData();

                if (fresh.id !== previous.id) {
                    this.fetchEarlier();
                }
            }
        },
        mounted() {
            // Event handling is now done through the handleMessage method
        },
        beforeDestroy() {
            // No cleanup needed for parent events
        },
        methods: {
            Can,
            handleMessage(msg) {
                if(msg.chat_id === this.chat.id) {
                    this.messages.push(msg);
                }
                if(msg.type === "close") {
                    console.log("CLOSE REQUEST");
                    this.$emit('archiveChat', msg.chat_id);
                }
            },
            async fetchData() {
                this.$store.commit('setLoading',true);
                await store.dispatch('chats/getMessages', { ApiClient: Vue.prototype.$ApiClient, departments: this.$store.getters.getUserData.departments, chatId: this.chat.id }).then((res) => {
                    if (res.status === 200) {
                        this.messages = res.data.data;
                    } else {
                        this.messages = [];
                    }
                }).catch((err) => {

                });
                this.$store.commit('setLoading',false);
            },
            async fetchEarlier() {
                const chatId = this.chat.id;
                this.showEarlier = false;
                this.earlier = [];

                if (this.chat.is_workfix_line !== true) {
                    return;
                }

                const outcome = describeApiOutcome(await store.dispatch('chats/getEarlierConversations', {
                    ApiClient: Vue.prototype.$ApiClient,
                    chatId
                }));

                if (outcome.ok && chatId === this.chat.id) {
                    this.earlier = outcome.data.data;
                }
            },
            dayOf(date) {
                return moment(date).format('DD/MM/YYYY');
            },
            async sendMessage(msg) {
                this.sending = true;
                const chatId = this.chat.id;
                const hadNoOwner = !this.chat.owner;
                const outcome = describeApiOutcome(await store.dispatch('chats/sendMessage',
                    {
                        ApiClient: Vue.prototype.$ApiClient,
                        departments: this.$store.getters.getUserData.departments,
                        chatId,
                        content: msg
                    }
                ), { fallback: CLAIMED_BY_SOMEONE_ELSE });
                this.sending = false;

                if (outcome.ok && this.$refs.input) {
                    this.$refs.input.clear();
                }

                this.reportSendOutcome(chatId, hadNoOwner, outcome);
            },
            async uploadFile(file) {
                this.$store.commit('setLoading',true);
                const chatId = this.chat.id;
                const hadNoOwner = !this.chat.owner;
                const outcome = describeApiOutcome(await store.dispatch('chats/sendFileMessage', {
                    ApiClient: Vue.prototype.$ApiClient,
                    departments: this.$store.getters.getUserData.departments,
                    file: file,
                    chatId
                }), { fallback: CLAIMED_BY_SOMEONE_ELSE });

                this.$store.commit('setLoading',false);
                this.reportSendOutcome(chatId, hadNoOwner, outcome);
            },
            reportSendOutcome(chatId, hadNoOwner, outcome) {
                const refused = NO_LONGER_YOURS_TO_ANSWER.includes(outcome.status);
                this.sendFailure = refused ? outcome.message : '';
                this.$emit('sent', { chatId, claimed: hadNoOwner && outcome.ok, refused });
            },
            async onInboxArchive() {
                const outcome = describeApiOutcome(await store.dispatch('chats/inboxArchive', {
                    ApiClient: Vue.prototype.$ApiClient,
                    departments: this.$store.getters.getUserData.departments,
                    chatId: this.chat.id
                }), { fallback: ARCHIVE_FAILED });

                this.sendFailure = outcome.ok ? '' : outcome.message;

                if (outcome.ok) {
                    this.$emit('archiveChat', this.chat.id);
                }
            },
            async onRequestCloseChat() {
                await store.dispatch('chats/sendCloseChatRequest', { 
                    ApiClient: Vue.prototype.$ApiClient, 
                    departments: this.$store.getters.getUserData.departments,
                    chatId: this.chat.id
                }).catch((err) => {

                });
            }
        }
    }
</script>

<style scoped>
    .messages-container {
        flex: 1 1 auto;
        overflow: auto;
        min-height: 0;
    }
    .send-failure {
        color: #D32F2F;
        font-size: 1.2rem;
    }
    .picked-up-note {
        color: #8d97ad;
        font-size: 1.2rem;
        font-style: italic;
    }
</style>